import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  ArrowRight,
  ChevronRight,
  ShieldCheck,
  HeartHandshake,
  Wrench,
  Headphones,
  Sparkles,
  Handshake,
  CreditCard,
  Monitor,
  Wallet,
  LifeBuoy,
  Search,
  MessagesSquare,
  Settings2,
  Quote,
} from "lucide-react";
import Counter from "@/components/Counter";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  GrowthIllustration,
  EcosystemIllustration,
  AnywhereIllustration,
  MissionIllustration,
  ProcessIllustration,
} from "@/components/illustrations/FintechIllustrations";

const services = [
  {
    icon: CreditCard,
    title: "Merchant Services",
    text: "We help businesses accept credit cards, debit cards, contactless payments, mobile payments, and online payments with secure processing solutions.",
  },
  {
    icon: Monitor,
    title: "POS Systems",
    text: "We provide modern POS systems and payment terminals designed to help businesses process transactions faster and operate more efficiently.",
  },
  {
    icon: Wallet,
    title: "Cash Discount & Dual Pricing",
    text: "We help eligible businesses explore programs that may reduce processing costs by offering clear cash and card pricing options.",
  },
  {
    icon: LifeBuoy,
    title: "Support & Setup",
    text: "From setup to training and ongoing support, Swipetronics helps merchants feel confident with their payment system.",
  },
];

const values = [
  {
    icon: ShieldCheck,
    title: "Honest Pricing",
    text: "Clearer payment options and straightforward explanations so business owners understand what they are paying.",
  },
  {
    icon: Wrench,
    title: "Reliable Equipment",
    text: "Modern POS systems and terminals built for daily business use.",
  },
  {
    icon: Sparkles,
    title: "Easier Switching",
    text: "We help guide merchants through the transition so switching processors feels simple and organized.",
  },
  {
    icon: Headphones,
    title: "Real Support",
    text: "Business owners deserve support from real people who can actually help when it matters.",
  },
  {
    icon: Settings2,
    title: "Custom Solutions",
    text: "Every business is different. Swipetronics helps match the right processing and POS setup to the business type.",
  },
  {
    icon: HeartHandshake,
    title: "Long-Term Partnership",
    text: "We are focused on building relationships, not just signing accounts.",
  },
];

const steps = [
  {
    icon: Search,
    title: "Review Your Current Setup",
    text: "We look at your current processing, POS system, and business needs.",
  },
  {
    icon: MessagesSquare,
    title: "Explain Your Options",
    text: "We show you possible options in simple language without confusing industry terms.",
  },
  {
    icon: Settings2,
    title: "Set Up the Right Solution",
    text: "We help with equipment, onboarding, setup, and training.",
  },
  {
    icon: Handshake,
    title: "Support You Long-Term",
    text: "After setup, we stay available to help with questions, issues, and future upgrades.",
  },
];

const stats = [
  { value: 7, suffix: "+", label: "Years of experience" },
  { value: 100, suffix: "%", label: "Real human support" },
  { value: 300, suffix: "+", label: "Locations served" },
  { value: 24, suffix: "/7", label: "Remote & on-site setup options" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo {...getSeoForPath("/about")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        {/* Subtle grid pattern across full hero */}
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />

        {/* Layered atmospheric glows that bleed across the entire hero */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />

        {/* Free-floating decorative shapes — span the whole header */}
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        {/* Full-hero particle field — flows behind text AND illustration */}
        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          {/* breadcrumbs */}
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">About Us</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            {/* LEFT — text */}
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  About Swipetronics
                </div>
                <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] leading-[1.05] font-extrabold text-white tracking-[-0.02em]">
                  Payment Processing Built Around{" "}
                  <span className="text-[var(--swipe-accent)]">Business Owners</span>
                </h1>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Swipetronics helps businesses simplify payments, upgrade their POS systems, and
                  get the honest support they need to operate with confidence.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/savings-analysis"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="about-hero-cta-savings"
                  >
                    Free Savings Analysis <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/contact"
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="about-hero-cta-contact"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* RIGHT — illustration blended into the hero (no card frame) */}
            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <AnywhereIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 1. MISSION */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-12 lg:gap-16 items-center">
            {/* LEFT — text */}
            <div>
              <Reveal>
                <div className="overline mb-5">Our Mission</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Payments should feel simple, honest, and built for the people running the business.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <div className="mt-8 relative">
                  <Quote className="absolute -left-2 -top-3 w-9 h-9 text-[var(--swipe-accent)]/25" strokeWidth={1.5} />
                  <p className="pl-8 text-[16px] md:text-[17px] text-slate-700 leading-[1.8]">
                    Our mission at Swipetronics is to help businesses accept payments with confidence
                    by providing reliable processing, modern POS solutions, honest pricing, and support
                    that is easy to reach. We believe business owners should be able to focus on
                    serving customers and growing their business — not trying to decode confusing
                    statements, hidden fees, or outdated equipment.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* RIGHT — illustration */}
            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 70% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={24}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <MissionIllustration className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. ABOUT SWIPETRONICS */}
      <section className="section bg-[var(--swipe-muted)] border-y border-line">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1.05fr] gap-12 lg:gap-20 items-center">
            <Reveal variant="slide-left">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 30% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={26}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <EcosystemIllustration className="relative w-full h-auto max-w-[560px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={80}>
              <div>
                <div className="overline mb-4">About Swipetronics</div>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
                  A merchant processing and POS company built around how businesses actually operate.
                </h2>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.75]">
                  Swipetronics is a merchant processing and POS systems company built to serve
                  businesses that want clearer pricing, better equipment, and a smoother payment
                  experience. We work with restaurants, delis, convenience stores, retailers,
                  salons, service businesses, mobile businesses, and e-commerce sellers to provide
                  payment solutions that fit how they actually operate.
                </p>
                <p className="mt-5 text-[16px] text-slate-600 leading-[1.75]">
                  Our approach is simple: review the current setup, explain the options clearly,
                  recommend the right solution, and support the business after setup. Whether a
                  merchant needs traditional processing, cash discount or dual pricing options, a
                  countertop terminal, mobile payment device, e-commerce payments, or a full POS
                  system, Swipetronics helps make the switch easier.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. WHO WE ARE */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-20 items-center">
            <Reveal variant="slide-left">
              <div>
                <div className="overline mb-4">Who We Are</div>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
                  Built to give business owners a better experience.
                </h2>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.75]">
                  Swipetronics was built with one goal: to give business owners a better experience
                  with merchant processing. Too many businesses deal with confusing rates, poor
                  communication, equipment issues, and processors that disappear after the sale.
                  We distinguish ourselves through clear, responsive, professional, and long
                  lasting client partnerships.
                </p>

                <div className="mt-10 grid grid-cols-2 gap-5">
                  {[
                    ["Clear", "No confusing rate sheets or hidden fees."],
                    ["Responsive", "Real people, real phone numbers."],
                    ["Professional", "Modern equipment that holds up daily."],
                    ["Built to last", "Long-term partnership, not a one-time sale."],
                  ].map(([t, d]) => (
                    <div key={t} className="border-l-2 border-[var(--swipe-accent)] pl-4">
                      <div className="font-display text-[15px] font-bold text-[var(--swipe-navy)]">{t}</div>
                      <div className="mt-1 text-[14px] text-slate-600 leading-relaxed">{d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 70% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={26}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <GrowthIllustration className="relative w-full h-auto max-w-[560px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. WHAT WE DO */}
      <section className="section bg-[var(--swipe-muted)] border-y border-line">
        <div className="container-swipe">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <div className="overline mb-4">What We Do</div>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
                A full set of services for modern businesses.
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 90}>
                  <div
                    className="group relative h-full rounded-2xl p-7 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)",
                    }}
                  >
                    {/* top accent bar */}
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                      style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }}
                    />
                    {/* corner glow */}
                    <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "rgba(0,114,255,0.22)", filter: "blur(40px)" }} />
                    <div className="relative">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3"
                        style={{
                          background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
                          boxShadow: "0 14px 28px -10px rgba(0,114,255,0.55)",
                        }}
                      >
                        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                      </div>
                      <h3 className="mt-6 font-display text-[19px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. WHY CHOOSE SWIPETRONICS */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Reveal>
              <div className="overline mb-4">Why Choose Swipetronics?</div>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
                Six reasons business owners choose us.
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={(i % 3) * 90}>
                  <div
                    className="group relative h-full rounded-2xl p-8 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden"
                    style={{
                      background:
                        i % 2 === 0
                          ? "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)"
                          : "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)",
                    }}
                  >
                    {/* top accent bar */}
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                      style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }}
                    />
                    {/* corner glow */}
                    <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "rgba(0,114,255,0.22)", filter: "blur(44px)" }} />
                    <div className="relative">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3"
                        style={{
                          background:
                            i % 2 === 0
                              ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)"
                              : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)",
                          boxShadow:
                            i % 2 === 0
                              ? "0 14px 28px -10px rgba(0,114,255,0.55)"
                              : "0 14px 28px -10px rgba(6,26,58,0.55)",
                        }}
                      >
                        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                      </div>
                      <h3 className="mt-6 font-display text-[19px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                        {v.title}
                      </h3>
                      <p className="mt-3 text-[15px] text-slate-600 leading-[1.7]">{v.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. HOW WE HELP — process */}
      <section className="section bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none grid-faint opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 50% at 80% 0%, rgba(0,114,255,0.18) 0%, transparent 60%)'
        }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1fr,1fr] gap-12 lg:gap-16 items-center mb-16">
            {/* LEFT — text */}
            <div className="max-w-3xl">
              <Reveal>
                <div className="overline mb-4" style={{ color: "#7DB6FF" }}>How We Help</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold leading-[1.12] text-white">
                  A clear, no-pressure process from review to long-term support.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[16px] text-white/70 leading-[1.75] max-w-xl">
                  We keep onboarding simple — review your current setup, explain your options in
                  plain language, set up the right solution, and stay available long after the
                  first transaction goes through.
                </p>
              </Reveal>
            </div>

            {/* RIGHT — process illustration */}
            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 70% 50%, rgba(0,114,255,0.22) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={24}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <ProcessIllustration className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.4)]" />
              </div>
            </Reveal>
          </div>

          <div className="relative">
            {/* connector line — desktop only */}
            <div className="hidden lg:block absolute left-0 right-0 top-[44px] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.title} delay={i * 120}>
                    <div className="relative group">
                      <div className="flex items-start gap-5">
                        <div className="relative shrink-0">
                          <div className="w-[88px] h-[88px] rounded-2xl bg-white/[0.04] backdrop-blur border border-white/10 flex items-center justify-center transition-all duration-300 group-hover:border-[var(--swipe-accent)] group-hover:bg-[var(--swipe-accent)]/10">
                            <Icon className="w-8 h-8 text-[var(--swipe-accent)]" strokeWidth={1.9} />
                          </div>
                          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[var(--swipe-accent)] text-white text-[13px] font-bold flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(0,114,255,0.6)]">
                            {i + 1}
                          </div>
                        </div>
                      </div>
                      <h3 className="mt-6 font-display text-[18px] font-extrabold leading-tight" style={{ color: "#FFFFFF" }}>
                        {s.title}
                      </h3>
                      <p className="mt-3 text-[14.5px] text-white/65 leading-[1.7]">{s.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 7. STATS */}
      <section className="bg-white border-y border-line">
        <div className="container-swipe py-20 md:py-24">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {stats.map((st, i) => (
              <Reveal key={st.label} delay={i * 90}>
                <div className="text-center lg:text-left relative">
                  <div className="text-[3rem] lg:text-[3.75rem] font-extrabold text-[var(--swipe-navy)] tracking-tight leading-none font-display-tight">
                    <Counter end={st.value} suffix={st.suffix} />
                  </div>
                  <div className="mt-3 text-[13px] uppercase tracking-[0.16em] font-bold text-[var(--swipe-accent)]">
                    {st.label}
                  </div>
                  <div className="mt-4 h-px w-12 mx-auto lg:mx-0 bg-gradient-to-r from-[var(--swipe-accent)] to-transparent" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,114,255,0.22) 0%, transparent 65%)'
        }} />
        <div className="container-swipe section relative">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <div className="overline mb-4" style={{ color: "#7DB6FF" }}>Let&apos;s talk</div>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2.25rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold leading-[1.08] text-white">
                Ready for a Better Payment Processing Experience?
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.75] max-w-2xl mx-auto">
                Whether you need lower processing costs, a new POS system, or a more reliable
                payment partner, Swipetronics is ready to help.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/get-started"
                  className="btn-primary-swipe text-[15px] py-3.5 px-7"
                  data-testid="about-cta-get-started"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="btn-outline-light text-[15px] py-3.5 px-7"
                  data-testid="about-cta-contact"
                >
                  Contact Us
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
